import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  V2AppShell,
  V2Sidebar,
  V2Topbar,
  V2BottomNav,
  V2Button,
  LoadingState,
  type V2NavItem,
  type V2BottomNavItem,
} from '@sakhtar/design-system/react';
import { getOperatorSession, operatorLogout } from '../../lib/operatorApi.js';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';

const GROUPS = [
  {
    label: 'کنترل‌پلتفرم',
    items: [
      {
        id: 'organizations',
        label: 'سازمان‌ها',
        icon: 'building' as const,
        href: '/internal/admin/organizations',
      },
      {
        id: 'plans',
        label: 'طرح‌ها',
        icon: 'subscription' as const,
        href: '/internal/admin/plans',
      },
      {
        id: 'ai-usage',
        label: 'پایش مصرف و سود یارها',
        icon: 'star' as const,
        href: '/internal/admin/ai/usage',
      },
      {
        id: 'ai-finance',
        label: 'حساب سود و زیان یارها',
        icon: 'invoice' as const,
        href: '/internal/admin/ai/finance',
      },
      {
        id: 'ai-pricing',
        label: 'قیمت‌گذاری یارها',
        icon: 'invoice' as const,
        href: '/internal/admin/ai/pricing',
      },
      {
        id: 'ai-quality',
        label: 'کیفیت و خطاهای یارها',
        icon: 'warn' as const,
        href: '/internal/admin/ai/quality',
      },
      {
        id: 'ai-monitoring',
        label: 'پایش اتصال سرویس‌های هوشمند',
        icon: 'globe' as const,
        href: '/internal/admin/ai/monitoring',
      },
      {
        id: 'ai-interest',
        label: 'پیش‌ثبت‌نام هوش مصنوعی',
        icon: 'star' as const,
        href: '/internal/admin/ai-assistant-interest',
      },
      {
        id: 'billing',
        label: 'خریدها و درگاه پرداخت',
        icon: 'invoice' as const,
        href: '/internal/admin/billing',
      },
      {
        id: 'notifications',
        label: 'اعلان‌های پیام‌رسان',
        icon: 'bell' as const,
        href: '/internal/admin/notifications',
      },
      {
        id: 'birthdays',
        label: 'تولدهای نزدیک',
        icon: 'calendar' as const,
        href: '/internal/admin/birthdays',
      },
      {
        id: 'short-links',
        label: 'لینک کوتاه و QR',
        icon: 'globe' as const,
        href: '/internal/admin/short-links',
      },
      { id: 'audit', label: 'رویدادها', icon: 'shield' as const, href: '/internal/admin/audit' },
      {
        id: 'ui-kit',
        label: 'کیت رابط کاربری',
        icon: 'database' as const,
        href: '/internal/admin/ui-kit',
      },
    ],
  },
];

const MOBILE_NAV: readonly Omit<V2BottomNavItem, 'active'>[] = [
  {
    id: 'organizations',
    label: 'سازمان‌ها',
    icon: 'building',
    href: '/internal/admin/organizations',
  },
  { id: 'plans', label: 'طرح‌ها', icon: 'subscription', href: '/internal/admin/plans' },
  {
    id: 'ai-usage',
    label: 'یارها',
    icon: 'star',
    href: '/internal/admin/ai/usage',
  },
  { id: 'billing', label: 'خریدها', icon: 'invoice', href: '/internal/admin/billing' },
  { id: 'audit', label: 'رویداد', icon: 'shield', href: '/internal/admin/audit' },
  { id: 'logout', label: 'خروج', icon: 'logout', href: '/internal/admin/login' },
];

export function OperatorShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'ready' | 'unauthenticated'>('loading');

  useEffect(() => {
    let cancelled = false;
    void getOperatorSession()
      .then((session) => {
        if (cancelled) return;
        setState(session.authenticated ? 'ready' : 'unauthenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setState('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (state === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <LoadingState />
      </div>
    );
  }
  if (state === 'unauthenticated') {
    return <Navigate to="/internal/admin/login" replace />;
  }

  const groups = GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      active: location.pathname === item.href || location.pathname.startsWith(`${item.href}/`),
    })),
  }));

  async function logout() {
    await operatorLogout();
    navigate('/internal/admin/login');
  }

  const bottomNavItems: V2BottomNavItem[] = MOBILE_NAV.map((item) => ({
    ...item,
    active:
      item.id === 'logout'
        ? false
        : location.pathname === item.href || location.pathname.startsWith(`${item.href}/`),
  }));

  return (
    <V2AppShell
      sidebar={
        <V2Sidebar
          brandLogoUrl={logoUrl}
          brandName="ساختار"
          groups={groups}
          renderLink={(item: V2NavItem, content) => (
            <Link key={item.id} to={item.href} style={{ textDecoration: 'none' }}>
              {content}
            </Link>
          )}
        />
      }
      topbar={
        <V2Topbar
          breadcrumb={[{ label: 'کنسول داخلی' }, { label: 'اپراتور' }]}
          utilities={
            <>
              <V2Button
                variant="ghost"
                size="sm"
                icon="globe"
                onClick={() => navigate('/internal/admin/ai/monitoring')}
                aria-label="پایش اتصال سرویس‌های هوشمند"
              >
                پایش اتصال
              </V2Button>
              <V2Button variant="ghost" size="sm" onClick={() => void logout()}>
                خروج
              </V2Button>
            </>
          }
        />
      }
      bottomNav={
        <V2BottomNav
          items={bottomNavItems}
          renderLink={(item, content) =>
            item.id === 'logout' ? (
              <button
                key={item.id}
                type="button"
                onClick={() => void logout()}
                style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
              >
                {content}
              </button>
            ) : (
              <Link key={item.id} to={item.href} style={{ textDecoration: 'none' }}>
                {content}
              </Link>
            )
          }
        />
      }
    >
      <Outlet />
    </V2AppShell>
  );
}

export function OperatorPage({
  title,
  leading,
  actions,
  children,
}: {
  title: string;
  /** Shown before the title — e.g. the organization's logo. */
  leading?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {leading}
          <h1 style={{ margin: 0, fontSize: 22 }}>{title}</h1>
        </div>
        {actions}
      </div>
      <div style={{ minWidth: 0, overflowX: 'auto' }}>{children}</div>
    </div>
  );
}
